import {
  useDataProvider,
  useGetIdentity,
  useNavigation,
  useNotification,
} from "@refinedev/core";

import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import type {
  PipelineItem,
  RunConfig,
  RunCostBreakdown,
  RunDetail,
  RunTriggerResponse,
} from "@/types/tina";

export interface TriggerRunVariables {
  sites: string[];
  limit?: number;
  config?: RunConfig;
}

export const useRunsRepository = () => {
  const { custom, getOne, getList } = useDataProvider()("tina");
  const { open } = useNotification();
  const { show } = useNavigation();
  const { data: identity } = useGetIdentity<IIdentity>();

  async function get(id: string): Promise<RunDetail> {
    const response = await getOne({ resource: "tina-runs", id });
    return response as unknown as RunDetail;
  }

  async function getCostBreakdown(id: string): Promise<RunCostBreakdown> {
    const response = await custom!({
      url: "",
      method: "get",
      meta: { urlPath: `runs/${id}/cost-breakdown` },
    });
    return response.data as RunCostBreakdown;
  }

  async function listItems(
    runId: string,
    pagination: { currentPage: number; pageSize: number } = {
      currentPage: 1,
      pageSize: 50,
    },
  ): Promise<{ data: PipelineItem[]; total: number }> {
    const response = await getList!({
      resource: "tina-runs",
      meta: { urlPath: `runs/${runId}/events` },
      pagination: { ...pagination, mode: "server" as const },
      filters: [],
      sorters: [],
    });
    return { data: response.data as PipelineItem[], total: response.total };
  }

  async function listAllItems(runId: string): Promise<PipelineItem[]> {
    const pageSize = 200;
    let currentPage = 1;
    let total = Number.POSITIVE_INFINITY;
    const items: PipelineItem[] = [];

    while (items.length < total) {
      const page = await listItems(runId, { currentPage, pageSize });
      items.push(...page.data);
      total = page.total;
      if (page.data.length === 0) break;
      currentPage += 1;
    }

    return items;
  }

  async function trigger(values: TriggerRunVariables): Promise<void> {
    Sentry.addBreadcrumb({
      category: "tina-runs.trigger",
      message: "Triggering run",
      level: "info",
      data: { sites: values.sites },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { ...values, triggered_by: identity?.id },
        meta: { urlPath: "runs" },
      });
      const result = response.data as RunTriggerResponse;
      open?.({
        type: "success",
        message: "Run triggered",
        description: `Seeded ${result.seeded.length} site(s)`,
      });
      show("tina-runs", result.run_id);
    } catch (error) {
      Sentry.captureException(error);
      notifyMutationError(open, "Trigger run", error);
      throw error;
    }
  }

  async function terminate(runId: string): Promise<RunDetail> {
    Sentry.addBreadcrumb({
      category: "tina-runs.terminate",
      message: "Terminating run",
      level: "warning",
      data: { runId },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        meta: { urlPath: `runs/${runId}/terminate` },
      });
      open?.({
        type: "success",
        message: "Run terminated",
        description: runId.slice(0, 8),
      });
      return response.data as RunDetail;
    } catch (error) {
      Sentry.captureException(error);
      notifyMutationError(open, "Terminate run", error);
      throw error;
    }
  }

  return {
    get,
    getCostBreakdown,
    listItems,
    listAllItems,
    trigger,
    terminate,
  };
};
